import { useState } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { authService } from '../../services/authService';
import { ROLES } from '../../utils/roles';
import { useAuth } from '../../contexts/AuthContext';

function NewEntreprise() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [entrepriseId, setEntrepriseId] = useState('');
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    secteur: '',
    autreSecteur: '',
    localisation: '',
    siteWeb: '',
    logo: '',
    telephone: '',
    email: ''
  });

  const [previewImage, setPreviewImage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'secteur' && value !== 'Autre' && { autreSecteur: '' })
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        setError("L'image ne doit pas dépasser 5MB");
        return;
      }

      setFormData(prev => ({
        ...prev,
        logo: file // Stocke le fichier directement au lieu de la version base64
      }));

      // Pour l'aperçu, on utilise toujours FileReader
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSubmit = {
        ...formData,
        secteur: formData.secteur === 'Autre' ? formData.autreSecteur : formData.secteur
      };
      delete dataToSubmit.autreSecteur;
      delete dataToSubmit.logo; // Supprimer le logo des données principales

      let logoUrl = null;
      if (formData.logo) {
        try {
          const formDataLogo = new FormData();
          formDataLogo.append('file', formData.logo);
          formDataLogo.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET_LOGO);

          const cloudinaryResponse = await fetch(
            `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/upload`,
            {
              method: 'POST',
              body: formDataLogo
            }
          );

          if (!cloudinaryResponse.ok) {
            throw new Error("Erreur lors de l'upload du logo");
          }

          const cloudinaryData = await cloudinaryResponse.json();
          logoUrl = cloudinaryData.secure_url;
        } catch (uploadError) {
          console.error('Erreur upload logo:', uploadError);
          throw new Error("Erreur lors de l'upload du logo");
        }
      }

      // Ajouter l'URL du logo aux données si disponible
      if (logoUrl) {
        dataToSubmit.logo = logoUrl;
      }

      // Utilisation de la bonne route selon le rôle
      const endpoint = userRole === ROLES.ADMIN 
        ? `${process.env.REACT_APP_API_BASE_URL}/admin/entreprises`
        : `${process.env.REACT_APP_API_BASE_URL}/pilote/entreprises`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(dataToSubmit)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la création de l\'entreprise');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/entreprises');
      }, 2000);
    } catch (err) {
      setError(err.message || "Une erreur est survenue lors de la création de l'entreprise");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/entreprises');
      }, 2000);
    } catch (err) {
      setError("Une erreur est survenue lors de la suppression de l'entreprise");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Ajouter une nouvelle entreprise
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Entreprise créée avec succès! Redirection...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            label="Nom de l'entreprise"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            multiline
            rows={4}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Secteur d'activité</InputLabel>
            <Select
              required
              name="secteur"
              value={formData.secteur}
              label="Secteur d'activité"
              onChange={handleChange}
            >
              <MenuItem value="Informatique">Informatique</MenuItem>
              <MenuItem value="Conseil">Conseil</MenuItem>
              <MenuItem value="Finance">Finance</MenuItem>
              <MenuItem value="Industrie">Industrie</MenuItem>
              <MenuItem value="Commerce">Commerce</MenuItem>
              <MenuItem value="Autre">Autre</MenuItem>
            </Select>
          </FormControl>

          {formData.secteur === 'Autre' && (
            <TextField
              required
              fullWidth
              label="Précisez le secteur"
              name="autreSecteur"
              value={formData.autreSecteur}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            required
            fullWidth
            label="Localisation"
            name="localisation"
            value={formData.localisation}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Site Web"
            name="siteWeb"
            value={formData.siteWeb}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Email de contact"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Téléphone"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 1 }}
              >
                {previewImage ? 'Changer le logo' : 'Ajouter un logo'}
              </Button>
            </label>

            {previewImage && (
              <Box sx={{ 
                position: 'relative', 
                width: 'fit-content', 
                margin: '0 auto'
              }}>
                <img
                  src={previewImage}
                  alt="Aperçu du logo"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '10px auto'
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    bgcolor: 'background.paper'
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/entreprises')}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              color="primary"
            >
              {loading ? 'Création...' : 'Créer l\'entreprise'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action supprimera également toutes les offres associées et est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button 
            onClick={() => handleDelete(entrepriseId)}
            color="error"
            disabled={loading}
            variant="contained"
          >
            {loading ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default NewEntreprise; 